import {Button, Modal} from "react-bootstrap";
import {Editor, type OnMount} from "@monaco-editor/react";
import * as monaco from 'monaco-editor';
import {
    type ChangeEvent,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from "react";
import type {OSImage} from "../../lib/ModelTypes.ts";
import AuthContext from "../../components/AuthContext.ts";
import {swal_util} from "../../lib/SwalUtil.ts";
import {useTranslation} from "react-i18next";

interface ContentProps {
    osImages: OSImage[];
    content: string;
}

const previewEditorOptions = {
    readOnly: true,
    minimap: {enabled: false},
};

export interface ModifyYamlPreviewModalRef extends HTMLDivElement {
    show(): void;
}

type PreviewRenderFuncs = "${name}" | "${instance_name}" | "${os_name}" | "${user.id}" | "${user.email}";


export const ModifyYamlPreviewModal = forwardRef<HTMLDivElement, ContentProps>(
    ({content, osImages}, ref) => {
        const {t} = useTranslation();
        const authContext = useContext(AuthContext);
        const modalRef = useRef<HTMLDivElement>(null);
        const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();
        const [renderedContent, setRenderedContent] = useState("");
        const [isShow, setShow] = useState(false);
        const [osInfo, setOsInfo] = useState({user_name: "ubuntu", os_name: "ubuntu generic"})
        const [instanceName, setInstanceName] = useState("Your Instance Name");
        const previewRenderFuncMap: Record<PreviewRenderFuncs, () => string> = useMemo(() => {
                return {
                    "${name}": () => osInfo.user_name,
                    "${instance_name}": () => instanceName,
                    "${os_name}": () => osInfo.os_name,
                    "${user.id}": () => authContext?.userInfo.id ?? "err",
                    "${user.email}": () =>  authContext?.userInfo.email ?? "err",
                }
            }, [osInfo, instanceName, authContext]
        );

        useImperativeHandle(ref, () => {
            return {
                ...modalRef.current!,
                show: () => {
                    if (osImages.length == 0) {
                        swal_util.show_error(t("CloudInit.informationMayBeInaccurateAsThereIsNoOsImageAvailable")).finally();
                    } else {
                        updateOsImage(osImages[0].id);
                    }

                    setShow(true);
                }
            }
        });

        const updateOsImage = useCallback((selectedOsId: string) => {
            if (osImages.length == 0) return;

            const idx = osImages.findIndex((osImage) => osImage.id === selectedOsId);
            if (idx < 0) return;
            const osImage = osImages[idx > 0 ? idx : 0];
            setOsInfo({
                user_name: osImage.user_name,
                os_name: osImage.name
            });
        }, [osImages]);

        const updateRenderedContent = useCallback(() => {
            if (!authContext) return;
            let initScriptText = content;

            for (const key in previewRenderFuncMap) {
                const regex = new RegExp(`\\${key}`, 'g');

                initScriptText = initScriptText.replace(regex, previewRenderFuncMap[key as keyof typeof previewRenderFuncMap]());
            }
            setRenderedContent(initScriptText);
        }, [previewRenderFuncMap, content, authContext]);

        useEffect(() => {
            updateRenderedContent();

        }, [authContext, updateRenderedContent, updateOsImage]);


        const onChangeInstanceName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
            setInstanceName(e.target.value);
        }, [setInstanceName]);

        const onMount = useCallback<OnMount>((editor) => {
            editorRef.current = editor;
        }, []);

        function closePreviewModal() {
            setShow(false)
        }

        const onChangeOSImage = useCallback(
            (e: ChangeEvent<HTMLSelectElement>) => {
                updateOsImage(e.currentTarget.value);
            }, [updateOsImage]
        )


        return (
            <Modal show={isShow} onHide={closePreviewModal} className="modal-lg" ref={modalRef}>
                <Modal.Header>
                    <Modal.Title><h6 className="text-primary pt-2">{t("CloudInit.preview")}</h6></Modal.Title>
                    <Button variant="white" className="btn_close" onClick={closePreviewModal}>X</Button>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group mb-4 col-12">
                        <h6 className="mb-2"><label htmlFor="instance-name">{t("createInstance.vmName")}</label></h6>
                        <input id="instance-name" type="text" className="form-control"
                               value={instanceName}
                               onChange={onChangeInstanceName}/>
                    </div>
                    <div className="form-group mb-4 col-12">
                        <h6 className="mb-2"><label htmlFor="osImage">{t("CloudInit.osImage")}</label></h6>
                        <select className="form-control" id="osImage" onChange={onChangeOSImage}>
                            {osImages.map((osImage) => (
                                <option key={osImage.id} value={osImage.id}>{osImage.name}</option>
                            ))}
                        </select>
                    </div>

                    <Editor
                        className="code-editor"
                        defaultLanguage="yaml"
                        options={previewEditorOptions}
                        onMount={onMount}
                        value={renderedContent}
                    />
                </Modal.Body>
            </Modal>
        )
    }
);